import * as Sentry from "@sentry/vue";
import { useAuthStore } from '@/stores/authStore';

export default class ErrorHandler {

	/**
	 * Captures a technical exception to Sentry with full HTTP and user context.
	 * Use in catch blocks for unexpected errors.
	 *
	 * 4xx errors (client/input errors) and connectivity errors are intentionally
	 * skipped — they are expected conditions, not application bugs.
	 *
	 * `blocksBoot` marks errors on the critical path of the first render (e.g. the
	 * getUser call the router awaits). Those are never noise: dropped silently they
	 * leave the user in front of a spinner that never goes away and nothing reaches
	 * Sentry, so they bypass the network-error filter.
	 */
	static handleError(error: any, options: { blocksBoot?: boolean } = {}): void {
		const { blocksBoot = false } = options;

		// 4xx: client/input errors — not bugs, skip Sentry
		if (error?.response?.status >= 400 && error?.response?.status < 500) {
			console.warn(
				`[${error.response.status}] ${error.config?.method?.toUpperCase() ?? ''} ${error.config?.url ?? ''} —`,
				error.response?.data
			);
			return;
		}

		// Network / connectivity / timeout — transient, skip Sentry
		// unless the failure is what is keeping the app from starting.
		if (this.isNetworkError(error) && !blocksBoot) {
			console.warn('Network error:', error.message ?? error);
			return;
		}

		const authStore = useAuthStore();
		Sentry.withScope((scope) => {
			if (blocksBoot) {
				scope.setTag('boot.blocking', 'true');
			}

			// User context
			if (authStore.isLoggedIn) {
				scope.setUser({
					id: authStore.user?.Id,
					email: authStore.user?.PersonEmail,
				});
			}

			// Searchable / filterable tags in Sentry dashboard
			if (error?.response?.status) {
				scope.setTag('http.status', String(error.response.status));
			}
			if (error?.config?.url) {
				scope.setTag('api.endpoint', error.config.url);
			}
			if (error?.config?.method) {
				scope.setTag('api.method', error.config.method.toUpperCase());
			}

			// Structured context visible in the Sentry event detail panel
			scope.setContext('api_request', {
				url: error?.config?.url ?? null,
				method: error?.config?.method?.toUpperCase() ?? null,
				status: error?.response?.status ?? null,
				response_body: error?.response?.data ?? null,
				error_code:
					error?.response?.data?.codiceErrore ??
					error?.response?.data?.codError ??
					null,
				error_description: error?.response?.data?.descrErrore ?? null,
			});

			scope.setLevel('error');
			Sentry.captureException(error);
		});
	}

	/**
	 * Captures a business/logic event (not a technical exception) to Sentry.
	 * Use when an unexpected business condition occurs that is worth tracking,
	 * even if no exception was thrown (e.g. invalid OTP, unexpected API response).
	 */
	static captureBusinessError(message: string, data?: any): void {
		const authStore = useAuthStore();
		Sentry.withScope((scope) => {
			if (authStore.isLoggedIn) {
				scope.setUser({
					id: authStore.user?.Id,
					email: authStore.user?.PersonEmail,
				});
			}
			if (data) {
				scope.setContext('details', { data });
			}
			Sentry.captureMessage(message, { level: 'warning' });
		});
	}

	/**
	 * Adds a debug breadcrumb to Sentry's event context.
	 * Use to trace the sequence of actions leading up to an error.
	 */
	static addLog(message: string, data?: any): void {
		Sentry.addBreadcrumb({
			category: 'app.log',
			message,
			level: 'info',
			data,
		});
	}

	private static isNetworkError(error: any): boolean {
		if (
			error?.message &&
			(error.message.includes('Network Error') ||
				error.message.includes('network error') ||
				error.message.includes('Failed to fetch') ||
				error.message.includes('timeout') ||
				error.message.includes('time out'))
		) {
			return true;
		}

		if (error?.isAxiosError) {
			// No response at all — connectivity issue
			if (!error.response) {
				return true;
			}
			// 5xx: server-side errors — treated as transient to avoid noise
			if (error.response.status >= 500 && error.response.status < 600) {
				return true;
			}
		}

		return false;
	}
}
